import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Checkbox, FormField, Input, Modal } from '@/components/ui';
import type { KeycloakAccountRow } from '../types';
import styles from './accountDialogs.module.css';

interface Props {
    row: KeycloakAccountRow | null;
    pending: boolean;
    error: string | null;
    onClose: () => void;
    onConfirm: (sub: string) => void;
}

/**
 * EAA-1820 (frame 73): single-account delete — username in the title, the two
 * consequences the audit/assignments systems impose, an optional assignments
 * acknowledgment, and typing the username (not the sub) before the destructive
 * button enables. Replaces the old sub-field + nested ConfirmButton shape.
 *
 * EAA-1804: footer swapped to the kit `Button` instead of hand-written
 * `<button className="portal-btn ...">`; the assignments-ack checkbox
 * swapped to the kit `Checkbox`, and the "type the username" field wrapped
 * in `FormField` + kit `Input`.
 */
export function DeleteAccountDialog({ row, pending, error, onClose, onConfirm }: Props) {
    const { t } = useTranslation();
    const [typed, setTyped] = useState('');
    const [assignmentsAck, setAssignmentsAck] = useState(false);

    const open = Boolean(row);
    const username = row?.username ?? '';
    const confirmed = typed.trim() === username;
    const placementCount = row?.placement?.count ?? 0;
    const needsAssignmentAck = placementCount > 0;

    const close = () => {
        setTyped('');
        setAssignmentsAck(false);
        onClose();
    };

    const canSubmit = confirmed && (!needsAssignmentAck || assignmentsAck);

    return (
        // EAA-1804: root debugging handle. "acc-delete-modal" rather than
        // "acc-delete-dialog" — that name is already taken by the inner form
        // div's grid-template-columns rule below.
        <Modal
            className="acc-delete-modal"
            open={open}
            onCancel={close}
            title={t('console.accounts.deleteModal.titleForUser', { username })}
            footer={
                <>
                    <span className={styles.deleteFooterNote}>{t('console.accounts.deleteModal.auditFooter')}</span>
                    <Button onClick={close} disabled={pending}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        danger
                        disabled={!canSubmit || pending || !row}
                        onClick={() => row && onConfirm(row.sub)}
                    >
                        {pending ? t('console.accounts.deleteModal.pending') : t('console.accounts.deleteModal.submit')}
                    </Button>
                </>
            }
        >
            <div className={`portal-admin-form ${styles.deleteDialog}`}>
                <p className="portal-admin-hint">{t('console.accounts.deleteModal.hintImmediate')}</p>

                <div className={styles.deleteConsequencesBox} role="note">
                    <strong>{t('console.accounts.deleteModal.notDeletedHeading')}</strong>
                    <ul className={styles.deleteConsequences}>
                        <li>
                            <strong className={styles.deleteTerm}>
                                {t('console.accounts.bulkDeleteModal.auditLogTerm')}
                            </strong>{' '}
                            {t('console.accounts.bulkDeleteModal.auditLogNote')}
                        </li>
                        <li>
                            <strong className={styles.deleteTerm}>
                                {t('console.accounts.bulkDeleteModal.assignmentsTerm')}
                            </strong>{' '}
                            {t('console.accounts.bulkDeleteModal.assignmentsNote')}
                        </li>
                    </ul>
                </div>

                {needsAssignmentAck && (
                    <Checkbox
                        className={styles.deleteAssignmentsAck}
                        checked={assignmentsAck}
                        onChange={(e) => setAssignmentsAck(e.target.checked)}
                    >
                        {t('console.accounts.deleteModal.assignmentsAck', { count: placementCount })}
                    </Checkbox>
                )}

                <FormField
                    label={
                        <Trans
                            i18nKey="console.accounts.deleteModal.confirmLabel"
                            values={{ username }}
                            components={{ code: <code /> }}
                        />
                    }
                >
                    <Input
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        placeholder={username}
                        autoComplete="off"
                        autoFocus
                    />
                </FormField>

                {error && <p className="portal-admin-error">{error}</p>}
            </div>
        </Modal>
    );
}

export default DeleteAccountDialog;
